import { API_ROOT_URL } from './config';

let accessToken: string | null = null;
let refreshPromise: Promise<{ access: string }> | null = null;

export function getAccessToken(): string | null {
	return accessToken;
}

export function setAccessToken(token: string): void {
	accessToken = token;
}

export function clearAccessToken(): void {
	accessToken = null;
}

export type LoginChallenge = {
	status: 'mfa_required' | 'mfa_setup_required';
	mfa_token: string;
};

export type TwoFASetup = {
	secret: string;
	otpauth_url: string;
	qr: string;
};

export type AuthSuccess = {
	access: string;
	user?: { id: number; username: string };
	recovery_codes?: string[];
};

async function postJSON(path: string, body: unknown) {
	const response = await fetch(`${API_ROOT_URL}${path}`, {
		method: 'POST',
		credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body)
	});
	const data = await response.json().catch(() => ({}));
	if (!response.ok) {
		throw new Error(
			(typeof data === 'object' && data !== null && 'detail' in data
				? String((data as { detail?: string }).detail)
				: null) || 'Request failed'
		);
	}
	return data;
}

// finalizeAuth stores the access token + username from a successful
// verify/enable response.
function finalizeAuth(data: AuthSuccess) {
	setAccessToken(data.access);
	if (data.user?.username) {
		sessionStorage.setItem('username', data.user.username);
	}
}

// login performs the password step. Two-factor is mandatory, so it never returns
// tokens — it returns a challenge telling the UI to verify a code or enroll.
export async function login(username: string, password: string): Promise<LoginChallenge> {
	return (await postJSON('/api/auth/login/', { username, password })) as LoginChallenge;
}

// loginVerify completes a login for an already-enrolled user.
export async function loginVerify(mfaToken: string, code: string): Promise<AuthSuccess> {
	const data = (await postJSON('/api/auth/login/verify/', {
		mfa_token: mfaToken,
		code
	})) as AuthSuccess;
	finalizeAuth(data);
	return data;
}

// twoFASetup begins first-time enrollment and returns the QR/secret to scan.
export async function twoFASetup(mfaToken: string): Promise<TwoFASetup> {
	return (await postJSON('/api/auth/2fa/setup/', { mfa_token: mfaToken })) as TwoFASetup;
}

// twoFAEnable confirms the first code, enabling 2FA and logging the user in. The
// response includes one-time recovery codes to show once.
export async function twoFAEnable(mfaToken: string, code: string): Promise<AuthSuccess> {
	const data = (await postJSON('/api/auth/2fa/enable/', {
		mfa_token: mfaToken,
		code
	})) as AuthSuccess;
	finalizeAuth(data);
	return data;
}

export async function refreshSession() {
	if (refreshPromise) {
		return refreshPromise;
	}

	refreshPromise = (async () => {
		const response = await fetch(`${API_ROOT_URL}/api/auth/refresh/`, {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' }
		});

		const data = await response.json().catch(() => ({}));
		if (!response.ok) {
			clearAccessToken();
			throw new Error(
				(typeof data === 'object' && data !== null && 'detail' in data
					? String((data as { detail?: string }).detail)
					: null) || 'Session expired'
			);
		}

		setAccessToken((data as { access: string }).access);
		return data as { access: string };
	})();

	try {
		return await refreshPromise;
	} finally {
		refreshPromise = null;
	}
}

export async function logout() {
	try {
		await fetch(`${API_ROOT_URL}/api/auth/logout/`, {
			method: 'POST',
			credentials: 'include'
		});
	} finally {
		clearAccessToken();
		sessionStorage.removeItem('username');
	}
}

export async function ensureSession(): Promise<boolean> {
	if (accessToken) {
		return true;
	}
	try {
		await refreshSession();
		return true;
	} catch {
		return false;
	}
}
